import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { api } from '@api/v1/client'
import objectService from '@api/v1/objectService'
import { inferFieldType } from '@utils/fieldTypes'

const EditField = ({ fieldId, label, dataType, value, onChange }) => {
  const ft = inferFieldType(value, label, dataType)
  const inputCls = 'w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-cb-accent/20 focus:border-cb-accent'
  const boolChecked = value === true || value === 'True'

  switch (ft) {
    case 'boolean':
      return (
        <div className="flex items-center gap-3 py-2">
          <button
            type="button"
            role="switch"
            aria-checked={boolChecked}
            aria-label={label}
            onClick={() => onChange(!boolChecked)}
            className={`relative w-10 h-5 rounded-full transition-colors ${
              boolChecked ? 'bg-cb-accent' : 'bg-gray-300'
            }`}
          >
            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
              boolChecked ? 'translate-x-5' : 'translate-x-0.5'
            }`} />
          </button>
          <span className="text-sm text-gray-600">
            {boolChecked ? 'Yes' : 'No'}
          </span>
        </div>
      )

    case 'date':
      return (
        <input
          type="date"
          value={value ? dayjs(value).format('YYYY-MM-DD') : ''}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
        />
      )

    case 'datetime':
      return (
        <input
          type="datetime-local"
          value={value ? dayjs(value).format('YYYY-MM-DDTHH:mm') : ''}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
        />
      )

    case 'currency':
      return (
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">$</span>
          <input
            type="number"
            step="0.01"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            className={`${inputCls} pl-7`}
          />
        </div>
      )

    case 'number':
      return (
        <input
          type="number"
          step="any"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
        />
      )

    case 'longtext':
      return (
        <textarea
          rows={3}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className={`${inputCls} resize-y`}
        />
      )

    default:
      return (
        <input
          type="text"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
        />
      )
  }
}

const CreateObjectModal = ({ typeId, typeName, onClose, onCreated }) => {
  const [values, setValues] = useState({})

  const { data: resp } = useQuery({
    queryKey: ['objectData', typeId],
    queryFn: () => api.get(`/objects/${typeId}`, { limit: 1 }),
    staleTime: 5 * 60 * 1000,
    enabled: !!typeId,
  })

  const fields = resp?.meta?.fields || []

  const saveMutation = useMutation({
    mutationFn: () => objectService.createObject(typeId, values),
    onSuccess: () => onCreated(),
  })

  const handleChange = (fieldId, val) => {
    setValues((prev) => ({ ...prev, [fieldId]: val }))
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-modal-title"
        className="bg-white rounded-xl shadow-xl border border-gray-100 w-full
        max-w-2xl max-h-[80vh] overflow-y-auto mx-4 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h2 id="create-modal-title" className="text-lg font-display text-gray-900">
            New {typeName}
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-gray-500 hover:text-gray-600 text-xl leading-none"
          >
            &times;
          </button>
        </div>

        {saveMutation.isError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">
            {saveMutation.error?.message || 'Failed to create record'}
          </div>
        )}

        {fields.length === 0 ? (
          <p className="text-sm text-gray-500 py-4">
            No field metadata available for this type.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {fields.map((f) => {
              const ft = inferFieldType(null, f.label, f.dataType)
              return (
                <div key={f.id} className={ft === 'longtext' ? 'sm:col-span-2' : ''}>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-500 mb-1">
                    {f.label}
                    <span className="text-[10px] text-gray-300 font-normal">{ft}</span>
                  </label>
                  <EditField
                    fieldId={f.id}
                    label={f.label}
                    dataType={f.dataType}
                    value={values[f.id] ?? ''}
                    onChange={(v) => handleChange(f.id, v)}
                  />
                </div>
              )
            })}
          </div>
        )}

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white
              border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || fields.length === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-cb-accent
              rounded-lg hover:bg-cb-accent/90 transition-colors shadow-sm
              disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saveMutation.isPending ? 'Creating...' : `Create ${typeName}`}
          </button>
        </div>
      </div>
    </div>
  )
}

export default CreateObjectModal
